import { formatValue } from '../../glossary/formatValue';

export const currency = (v: number) => formatValue(v, 'currency');

// Display the global 1-based month number from the start of LTC (not
// month-in-year), so "Month 20" stays "Month 20" rather than wrapping
// back to "Month 8".
export const monthDisplay = (monthIndex: number): number => monthIndex + 1;

// Per-claimant 1-based "Month of LTC" map. For a joint policy, partner rows
// carry a `monthIndex` aligned to the shared calendar anchor (the earliest
// claim across both spouses) — so a partner whose claim starts 24 months
// after the client first appears at `monthIndex 24` and naively reads as
// "Month 25." This map remaps each row to the 1-based count *within that
// claimant's own LTC stream*: partner's first care row → 1, second → 2, and
// so on. Single-life rows (no `claimant`) keep `monthIndex + 1` behavior.
//
// Computed from the full projection so the count is consistent across the
// pre-payout and payout slices the breakdown view splits at the first
// paying month. The map is sparse-safe — callers fall back to
// `monthDisplay(monthIndex)` on miss to handle rows from other views.
//
// Keyed by `claimant + monthIndex`, NOT monthIndex alone: when the two
// spouses' care overlaps in calendar time their rows share a `monthIndex`
// (the merged stream interleaves them), so a monthIndex-only key would let one
// claimant's row overwrite the other's relative count — making the later
// claimant read as the other's (much higher) month number.
export function computeClaimantRelativeMonthMap(
  months: MonthlyEntry[],
): Map<string, number> {
  const counts = new Map<string, number>();
  const out = new Map<string, number>();
  for (const m of months) {
    const key = m.context.claimant ?? '__single__';
    const next = (counts.get(key) ?? 0) + 1;
    counts.set(key, next);
    out.set(claimantMonthKey(m.context.claimant, m.context.monthIndex), next);
  }
  return out;
}

// Composite key disambiguating overlapping joint rows that share a `monthIndex`
// (each spouse's care month). Single-life rows (no claimant) collapse to a
// stable `__single__:N` key. Used anywhere a per-row map is keyed by month.
export function claimantMonthKey(
  claimant: MonthContext['claimant'],
  monthIndex: number,
): string {
  return `${claimant ?? '__single__'}:${monthIndex}`;
}

// Stable, collision-free React key for a projection row. Joint projections
// interleave both spouses, so two rows can share a `monthIndex` when their care
// overlaps — keying a row list by `monthIndex` alone collides and makes React
// mis-reconcile (duplicated / swapped rows). Including the claimant disambiguates.
export function monthRowKey(month: MonthlyEntry): string {
  return `${month.context.claimant ?? 'self'}-${month.context.monthIndex}`;
}

// Resolve a row's displayed "Month N" against the per-claimant map, falling
// back to the legacy absolute count when the row is not in the map (e.g. the
// map wasn't provided, or the row is from a different projection slice).
// `claimant` must be passed alongside `monthIndex` so overlapping joint rows
// resolve to their own claimant's count, not whichever shared the monthIndex.
export function resolveMonthDisplay(
  monthIndex: number,
  claimant: MonthContext['claimant'],
  monthDisplayMap?: Map<string, number>,
): number {
  return monthDisplayMap?.get(claimantMonthKey(claimant, monthIndex)) ?? monthDisplay(monthIndex);
}
